/**
 * Matched accept · hint · size presets for FileDropZone surfaces (LF-4771).
 *
 * A drop zone has three independent knobs that are easy to let drift apart:
 * the native picker filter (`accept`), the visible types/size line (`hintText`),
 * and the enforced client cap (`maxSizeBytes`). Author them together here and
 * have each call site spread ONE preset, so a surface can never advertise one
 * thing and accept another.
 *
 * **`accept` is no longer authored — it is derived (VALE-1150).** A preset declares the backend
 * file type(s) it uploads to and, where it deliberately offers less than they accept, the format
 * groups it narrows to; `acceptStringForSlugs` computes the string from the generated backend
 * table. That makes the old failure impossible rather than merely detectable: before VALE-209
 * nothing connected the two, so `documents` offered HEIC to four destinations while only two
 * accepted it, and an iPhone photo passed the picker, passed every client-side check, and was
 * refused after the S3 PUT on "Add document" and the AI Scribe Context tab.
 *
 * **The rule the `narrowTo` entries follow:** a derived `accept` never offers a format the
 * destination rejects (the derivation guarantees that), and never offers one this surface's
 * `hintText` does not promise. So a preset whose hint enumerates formats narrows to exactly
 * those, and a preset whose hint is plain language ("Photos or documents") takes the full
 * intersection. Anything wider is a product decision about that surface, not a consequence of
 * binding to the backend, and belongs in its own ticket.
 *
 * **Size is standardized on `MAX_FILE_SIZE` (20 MB)** — the only server-grounded
 * cap in the system: it is the portal-intake default and the global frontend
 * limit. The authenticated `/files/pre-signed-url` → S3 PUT enforces no size at
 * all (the presigned PUT carries no content-length condition; upload-complete
 * only rejects 0-byte files), so these caps are purely client-side UX. The one
 * justified exception is 50 MB for wills in the portal — the `clientIntakeUpload`
 * preset below (LF-5791).
 */

import {
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_WILL,
  MAX_FILE_SIZE_WILL_MB,
  MAX_SIGNATURE_SIZE
} from '@/lib/constants/file-limits';
import {
  acceptStringForSlugs,
  formatGroups,
  type UploadableFileTypeSlug
} from '@/lib/constants/file-type-accept';

/** What a preset declares. `accept` is added by {@link withDerivedAccept}, never written here. */
interface FileAcceptPresetDefinition {
  /** Human-readable types/size line shown in the drop surface. */
  hintText: string;
  /** Client-side size ceiling in bytes. */
  maxSizeBytes: number;
  /**
   * The backend file type(s) a surface using this preset uploads against — the declaration the
   * whole preset now derives from, and the one whose absence let `accept` drift from the server
   * (VALE-209).
   *
   * `accept` is a promise about what the upload will take, and only the destination type can
   * keep it. Listing several means the surface can only offer their intersection, which is
   * exactly what had gone wrong: `documents` offered HEIC to four destinations and only two
   * accepted it.
   */
  fileTypeSlugs: readonly UploadableFileTypeSlug[];
  /**
   * The format groups this surface offers, when it deliberately offers **less** than its
   * destinations accept. Omit to offer the full intersection.
   *
   * Narrowing is always safe — the server would have accepted more — so this can only make a
   * picker stricter. Every use below names the reason.
   */
  narrowTo?: readonly string[];
}

export interface FileAcceptPreset extends FileAcceptPresetDefinition {
  /** Native `<input accept>` string — the picker filter + drop validation. Derived. */
  accept: string;
}

/** Add each definition's derived `accept`, preserving the registry's exact key types. */
const withDerivedAccept = <
  T extends Record<string, FileAcceptPresetDefinition>
>(
  definitions: T
): { [K in keyof T]: T[K] & { accept: string } } =>
  Object.fromEntries(
    Object.entries(definitions).map(([name, definition]) => [
      name,
      {
        ...definition,
        accept: acceptStringForSlugs(
          definition.fileTypeSlugs,
          definition.narrowTo
        )
      }
    ])
  ) as { [K in keyof T]: T[K] & { accept: string } };

/**
 * The registry. Spread a preset onto `<FileDropZone {...preset} />` and feed the
 * same `accept`/`maxSizeBytes` to `useFileDropZoneState`. Add a new key here
 * (never a one-off literal at a call site) when a surface needs a new combo.
 */
export const FILE_DROP_ZONE_ACCEPT = withDerivedAccept({
  /**
   * Supporting documents — a scan, a photo, or an office document. Used by Intake Context and
   * the Scribe session Context tab (both feed the AI summary). `hintText` size is derived from
   * `MAX_FILE_SIZE_MB` so the displayed number and the enforced cap stay in lockstep.
   *
   * No `narrowTo`: the hint promises "documents" generically, and the matter Add-document dialog
   * has always offered Word and Excel onto this same `additional-document` type. Deriving the
   * full intersection is what makes the two doors agree.
   */
  documents: {
    /*
      **Plain language, not a format list.** The native picker's `accept` filter
      already greys out anything unsupported and a drag-drop of the wrong thing
      raises a validation error naming the problem, so enumerating the types
      mostly informs people who cannot act on the information. `HEIC` was the
      clearest case: it is what every iPhone photo actually is, and it is the one
      entry in the list nobody recognises as describing their own file.

      The size cap stays, because that one IS actionable — a 30 MB scan can be
      re-exported smaller. It is derived from `MAX_FILE_SIZE_MB` so the number
      shown and the number enforced cannot drift.
    */
    hintText: `Photos or documents, up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: [
      'additional-document',
      'ai-scribe-upload',
      'client-intake-upload',
      'lead-document'
    ]
  },
  /**
   * Identity documents (primary / secondary ID) — a PDF or a photo. Used by the
   * contacts ID-upload surfaces. Size standardized on `MAX_FILE_SIZE` (20 MB) to
   * match those surfaces' current behaviour; the per-fileType registry's older
   * 5 MB value is the stricter alternative if we later want to tighten (review
   * call — flagged in LF-4854 / plan `[FE-8]`).
   *
   * Also accepts a forwarded Outlook email (.eml/.msg, LF-6125) — the backend
   * unwraps it into its attachments and checks THOSE against this same slot's
   * allowed types, so an email carrying a licence photo satisfies this ID slot
   * exactly as a directly-uploaded photo would. That comes from the derivation:
   * `primary-id` sets `allowsEmailAttachmentExtraction`.
   */
  identityDocument: {
    hintText: `PDF, JPG, PNG, or a forwarded email (.eml/.msg), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['primary-id']
  },
  /**
   * A wet-ink document scanned back in — the signed copy of a declaration
   * collected offline. PDF, or a forwarded email (.eml/.msg, LF-6125) whose
   * unwrapped attachment is itself a PDF — see `identityDocument` above.
   *
   * Narrowed away from the `.docx` that `distribution-declaration` also accepts: this door is
   * for the signed copy that came back from a printer and a pen, and a Word file cannot be one.
   * The `wordTemplate` preset below is the same type's other door, for the unsigned template.
   */
  signedDocument: {
    hintText: `PDF, or a forwarded email (.eml/.msg), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['distribution-declaration'],
    narrowTo: formatGroups('pdf', 'email')
  },
  /**
   * Client-intake portal — the executor's "additional documents" upload on the
   * end-of-intake confirmation page (LF-5791). Public magic-link surface: PDF or
   * images (HEIC/HEIF included — transcoded to JPEG server-side). Sized at 50 MB
   * to match the portal's `documentType: 'will'` backend cap (`MAX_FILE_SIZE_WILL`).
   *
   * HEIC files frequently report an empty `file.type`, so the derived `accept` carries their
   * extensions alongside the MIME types — see `MIME_TYPES_NEEDING_EXTENSION_FALLBACK`. For the
   * same reason, a caller must resolve the type it SENDS through `resolveUploadMimeType` — an
   * accept check can shrug at a blank `file.type`, an API that validates the MIME against a
   * fixed list cannot (VALE-925).
   *
   * Also accepts a forwarded email (.eml/.msg, LF-6125) — unwrapped server-side
   * into its attachments — but ONLY on this generic "additional documents" slot,
   * never on the portal's death-certificate/ID/will slots (see
   * `PortalIntakeFormService.requestUpload`'s documentType gate).
   *
   * Narrowed away from the Word and Excel formats `client-intake-upload` also accepts: this is a
   * grieving executor on a phone, following a hint that names four formats. Offering them here
   * is a product decision about the portal, not a consequence of binding to the backend.
   */
  clientIntakeUpload: {
    hintText: `PDF, JPG, PNG, HEIC, or a forwarded email (.eml/.msg), up to ${MAX_FILE_SIZE_WILL_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE_WILL,
    fileTypeSlugs: ['client-intake-upload'],
    narrowTo: formatGroups('pdf', 'image', 'phonePhoto', 'email')
  },
  /**
   * Client-intake portal — the typed document-capture slots (death certificate,
   * deceased's ID, executor's ID) on the "Got any of these nearby?" step.
   *
   * Same narrowing as `clientIntakeUpload` above and for the same reason, minus the email
   * formats: `PortalIntakeFormService.requestUpload` rejects an email file on any slot other
   * than the generic "additional documents" one, so offering it here would only move the
   * refusal to the server. 20 MB is the backend's `MAX_FILE_SIZE_DEFAULT` for these three types.
   */
  clientIntakeTypedDocument: {
    hintText: `PDF, JPG, PNG or HEIC, up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['client-intake-upload'],
    narrowTo: formatGroups('pdf', 'image', 'phonePhoto')
  },
  /**
   * Client-intake portal — the will slot on the same document-capture step.
   * Identical to `clientIntakeTypedDocument` but for the 50 MB cap, which is
   * what `maxFileSizeForDocumentType('will')` allows on the backend: a will is
   * commonly photographed page by page or scanned as one large multi-page PDF.
   */
  clientIntakeWill: {
    hintText: `PDF, JPG, PNG or HEIC, up to ${MAX_FILE_SIZE_WILL_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE_WILL,
    fileTypeSlugs: ['client-intake-upload'],
    narrowTo: formatGroups('pdf', 'image', 'phonePhoto')
  },
  /**
   * A firm's own version of a draft distribution statement, replacing the one Vale rendered.
   *
   * Word or PDF, and both for a reason: a `.docx` stays editable in Word and is converted to the
   * PDF the executor receives; a PDF is taken as the finished document. Nothing is rendered from
   * it, so there are no merge tags to preserve and no reason to refuse either.
   */
  statementDocument: {
    hintText: `Word (.docx) or PDF, up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['distribution-statement'],
    narrowTo: formatGroups('pdf', 'docx')
  },
  /**
   * A Word document the backend will render as an **ad-hoc template** — the
   * lead cost agreement's upload door (LF-6012 / LEADS-45).
   *
   * `.docx` alone, and that is a hard requirement rather than a preference: the
   * document goes through docxtemplater so its `<<signatureBlock>>` resolves
   * into the invisible anchors Annature searches the PDF text layer for. A PDF
   * would have nothing to render and no anchor to place a signature on, so
   * offering one here would only move the refusal to the server. Note `docx` rather than
   * `word`: `lead-cost-agreement` also takes a legacy `.doc`, which docxtemplater cannot open.
   */
  wordTemplate: {
    hintText: `Word (.docx), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['distribution-declaration', 'lead-cost-agreement'],
    narrowTo: formatGroups('docx')
  },
  /**
   * A firm member's own handwritten signature, uploaded on the account page — an image of a
   * mark on paper, so an image and never a PDF.
   *
   * It earns a preset because the account card was the last upload surface still typing its
   * `accept` inline, which is what the note at the top of this file forbids: a literal at a
   * call site is invisible to the reconciliation test and to anyone auditing what the app
   * offers (VALE-209). `hintText` names the formats because "PNG or JPG on a white
   * background" is genuinely actionable advice for a scanned signature.
   */
  handwrittenSignature: {
    hintText: 'PNG or JPG, ideally on a white background',
    maxSizeBytes: MAX_SIGNATURE_SIZE,
    fileTypeSlugs: ['handwritten-signature']
  },
  /**
   * Correspondence a lawyer is filing into a Communications thread by hand —
   * an Outlook message, or the scan of a letter that arrived by post (VALE-597).
   *
   * The email formats are **first-class here, not a forwarding convenience** as they
   * are on `identityDocument` / `signedDocument`: on those slots an `.eml` is a
   * wrapper the backend unwraps to reach the document inside, whereas here the
   * message IS the item being filed and its attachments are incidental. That is why
   * this needs its own preset rather than reusing one of theirs.
   *
   * The derived `accept` lists `.eml`/`.msg` alongside the MIME types on purpose: a `.msg`
   * dragged out of Outlook routinely reports `application/octet-stream` (Windows without Outlook
   * registered, and most Linux browsers), so an extension match is the only reliable picker
   * filter for it. The backend confirms the format by parsing the bytes either way.
   *
   * `hintText` names the formats rather than saying "documents", against the
   * plain-language reasoning on `documents` above, because here the format IS the
   * actionable instruction: new Outlook cannot drag a message into a browser, so a
   * user has to know that "save as .eml, then pick it" is the move. Narrowed to what that
   * sentence promises, leaving out the Word and Excel formats `imported-correspondence` also
   * takes.
   */
  correspondenceImport: {
    hintText: `Outlook message (.msg, .eml) or a scanned letter (PDF, JPG, PNG), up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['imported-correspondence'],
    narrowTo: formatGroups('email', 'pdf', 'image')
  },
  /**
   * The firm's own trust account statement, uploaded from the Estate account's rail
   * (VALE-1718). PDF only: the backend partitions the pages by each masthead before
   * anything is stored, and that needs a PDF.
   */
  trustStatement: {
    hintText: `PDF, up to ${MAX_FILE_SIZE_MB} MB`,
    maxSizeBytes: MAX_FILE_SIZE,
    fileTypeSlugs: ['trust-account-statement']
  }
} satisfies Record<string, FileAcceptPresetDefinition>);
